/**
 * Tiny in-process pub/sub for cross-screen data invalidation.
 *
 * Use case: when a mutation screen (Add/Edit/Delete) successfully saves, it
 * calls `invalidate('my-organization')`. Any screen that previously called
 * `subscribe('my-organization', fn)` gets its listener fired and can refetch
 * its data — even if the mutation screen used `navigation.goBack()` instead
 * of explicitly navigating with params.
 *
 * This is a trivially small alternative to React Query's invalidateQueries
 * for the paths where we don't yet use `useQuery`. Migratable: when a screen
 * is converted to React Query, replace `invalidate('key')` with
 * `queryClient.invalidateQueries(['key'])` at the mutation call sites and
 * drop the subscription on the list screen.
 *
 * Usage:
 *
 *   // On the list screen:
 *   useEffect(() => subscribe('my-organization', () => refetch()), [refetch]);
 *
 *   // On the mutation screen, after a successful save:
 *   invalidate('my-organization');
 *   navigation.goBack();
 */

export type InvalidationKey =
  | 'my-organization'
  | 'goals'
  | 'initiatives'
  | 'kpis'
  | 'processes'
  | 'services'
  | 'unit-goals';

type Listener = () => void;

const listeners = new Map<InvalidationKey, Set<Listener>>();

/**
 * Subscribe to invalidation events for a given key. Returns an unsubscribe
 * function — call it from your `useEffect` cleanup to avoid leaks.
 */
export function subscribe(key: InvalidationKey, listener: Listener): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(listener);
  return () => {
    set!.delete(listener);
  };
}

/**
 * Fire all listeners registered for a key. Safe to call with no subscribers
 * (no-op). Listener errors are logged but never propagated so one broken
 * subscriber can't block the others.
 */
export function invalidate(key: InvalidationKey): void {
  const set = listeners.get(key);
  if (!set) return;
  set.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.warn(`[dataInvalidation] listener for "${key}" threw:`, err);
    }
  });
}
